import React, { useRef, useState, useEffect } from 'react';
import { Flex, DropDownMenu, ClickableItem, ButtonSelect } from '@upsales/components';
import { Icon } from '@upsales/components';
import type {
	EditWidgetRowProps,
	WidgetDataSourceProps,
	WidgetRow
} from 'App/components/hooks/integrations/externalIntegrationTypes';
import { PrimaryButton } from '@upsales/components/Buttons';
import { useTranslation } from 'Components/Helpers/translate';
import UiWidgetComponent from './UiWidgetComponent';
import './UiWidgetRow.scss';
import BemClass from '@upsales/components/Utils/bemClass';

const supportedWidgetTypes = ['Text', 'Title', 'EmptyRow', 'Separator', 'Conditional', 'Date'];

export const EditUiWidgetRow = ({ row, onUpdate }: EditWidgetRowProps) => {
	const classes = new BemClass('EditUiWidgetRow');
	const { t } = useTranslation();
	const [children, setChildren] = useState<WidgetRow[]>(row.settings?.children || []);
	const [justifyContent, setJustifyContent] = useState<string>(row.settings?.justifyContent);
	const [alignItems, setAlignItems] = useState<string>(row.settings?.alignItems);
	const [gap, setGap] = useState<string>(row.settings?.gap);
	const rowRef = useRef<HTMLDivElement>(null);

	const draftChanges = useRef<WidgetRow>({ ...row });
	const lastSyncedRow = useRef<string>('');

	// Keep draftChanges.current in sync with the current row data
	useEffect(() => {
		draftChanges.current = {
			...row,
			settings: {
				...row.settings,
				children: children,
				justifyContent: justifyContent,
				alignItems: alignItems,
				gap: gap
			}
		};
	}, [JSON.stringify(row), children, justifyContent, alignItems, gap]);

	// Sync local state with row prop changes
	useEffect(() => {
		const rowString = JSON.stringify(row.settings);
		if (rowString !== lastSyncedRow.current) {
			lastSyncedRow.current = rowString;
			setChildren(row.settings?.children || []);
			setJustifyContent(row.settings?.justifyContent);
			setAlignItems(row.settings?.alignItems);
			setGap(row.settings?.gap);
		}
	}, [JSON.stringify(row.settings)]);

	const handleChange = (key: string, value: any) => {
		// Update the local state first
		if (key === 'children') {
			setChildren(value);
		} else if (key === 'justifyContent') {
			setJustifyContent(value);
		} else if (key === 'alignItems') {
			setAlignItems(value);
		} else if (key === 'gap') {
			setGap(value);
		}

		// Update draftChanges with the new value
		draftChanges.current = {
			...draftChanges.current,
			settings: {
				...draftChanges.current.settings,
				[key]: value
			}
		};

		onUpdate(draftChanges.current);
	};

	const handleAddChild = (widgetRowType: string) => {
		const childSettings: WidgetRow['settings'] = {};
		if (row.settings?.index) {
			childSettings.index = row.settings.index;
		}
		const newChildren = [...children, { type: widgetRowType, settings: {}, sortOrder: children.length + 1 }];
		setChildren(newChildren);
		handleChange('children', newChildren);
	};

	const justifyContentOptions = [
		{ icon: 'align-left', value: 'flex-start', tooltip: t('default.alignLeft') },
		{ icon: 'align-center', value: 'center', tooltip: t('default.alignCenter') },
		{ icon: 'align-right', value: 'flex-end', tooltip: t('default.alignRight') },
		{ icon: 'arrows-h', value: 'space-between', tooltip: t('default.alignSpaceBetween') },
		{ icon: 'indent', value: 'space-around', tooltip: t('default.alignSpaceAround') }
	] as const;

	const alignItemsOptions = [
		{ icon: 'arrow-up', value: 'flex-start', tooltip: t('default.alignTop') },
		{ icon: 'arrows-alt-v', value: 'center', tooltip: t('default.alignCenter') },
		{ icon: 'arrow-down', value: 'flex-end', tooltip: t('default.alignBottom') }
	] as const;

	const gapOptions = ['0', '1', '2', '3', '4', '5', '6', '7', '8'].map(gap => ({
		title: gap,
		value: gap,
		tooltip: t('default.gapDistance')
	}));

	return (
		<Flex direction="column" gap="u2" ref={rowRef} className={classes.b()}>
			<Flex className={classes.elem('combinedButtonSelect').b()} direction="column">
				<Flex>
					<ButtonSelect
						value={justifyContent || 'flex-start'}
						className={classes.elem('justifyContent').b()}
						onChange={value => {
							setJustifyContent(value);
							handleChange('justifyContent', value);
						}}
						options={justifyContentOptions}
					/>
				</Flex>
				<Flex>
					<ButtonSelect
						value={alignItems || 'flex-start'}
						className={classes.elem('alignItems').b()}
						onChange={value => {
							setAlignItems(value);
							handleChange('alignItems', value);
						}}
						options={alignItemsOptions}
					/>
				</Flex>
			</Flex>

			<Flex>
				<ButtonSelect
					value={gap || '0'}
					onChange={value => {
						setGap(value);
						handleChange('gap', value);
					}}
					options={gapOptions}
				/>
			</Flex>

			<Flex gap="u2">
				<DropDownMenu
					arrowed
					align="left"
					anchor={document.querySelector('.ExternalIntegrationGlobalSettings')}
					renderTrigger={(expanded, setExpanded) => (
						<PrimaryButton
							size="sm"
							onClick={e => {
								e.stopPropagation();
								setExpanded(e);
							}}
						>
							<Flex gap="u2" alignItems="center">
								{t('default.add')}
								<Icon name={expanded ? 'chevron-up' : 'chevron-down'} />
							</Flex>
						</PrimaryButton>
					)}
				>
					{closeDropdown => (
						<Flex direction="column" gap="u2" className="UiWidgetRow__dropdown">
							{supportedWidgetTypes.map(type => (
								<ClickableItem
									key={type}
									title={type}
									onClick={() => {
										handleAddChild(type);
										closeDropdown();
									}}
								/>
							))}
						</Flex>
					)}
				</DropDownMenu>
			</Flex>
		</Flex>
	);
};

const UiWidgetRow: React.FC<WidgetDataSourceProps> = ({
	row,
	widgetExternalId,
	dataSources,
	widgetSettings,
	widgetType
}) => {
	const alignItems = row.settings?.alignItems;
	const justifyContent = row.settings?.justifyContent;
	const gap = row.settings?.gap;
	const children = row.settings?.children;

	return (
		<Flex
			className="UiWidget__row"
			alignItems={alignItems}
			justifyContent={justifyContent}
			gap={gap ? `u${gap}` : undefined}
		>
			{children?.map((child: any, idx: number) => {
				const childSettings = child?.settings ?? {};
				if (row.settings?.index !== undefined) {
					childSettings.index = row.settings.index;
				}
				child.settings = childSettings;

				return (
					<UiWidgetComponent
						key={`${row.id}-${child.id}-${idx}`}
						row={child}
						widgetExternalId={widgetExternalId}
						dataSources={dataSources}
						widgetSettings={widgetSettings}
						widgetType={widgetType}
					/>
				);
			})}
		</Flex>
	);
};

export default UiWidgetRow;
